// @ts-ignore
import { useObserveSizeContext } from "@pathwright/ui/src/components/observers/ObserveSizeContext"

import { Box } from "@chakra-ui/react"
import React from "react"
import { usePrevious } from "react-use"
import AugmentationToolbar, {
  AugmentationToolbarProps
} from "./AugmentationToolbar"
import { usePanelSize } from "./useAugmentationPanel"

type ToolbarPosition = "bottom" | "side"
type AugmentationPositionedToolbarProps = AugmentationToolbarProps & {
  allowOverlay?: boolean
  gutter?: number
  offset?: { left?: number } | { right?: number }
}

// Rendering the toolbar based on screen size.
// - "full": render at bottom of screen
// - sm+: render to the right side of screen
export function useToolbarPosition<T>(
  breakpointOptions: Record<ToolbarPosition, T>,
  allowOverlay?: boolean
): T {
  const panelSize = usePanelSize({ allowOverlay })
  const position = panelSize === "full" ? "bottom" : "side"
  return breakpointOptions[position]
}

// Eh, a lot of work just to get the toolbar positioned sticky
// next to top of registrations list.
const ToolbarSide = React.forwardRef<
  HTMLDivElement,
  AugmentationPositionedToolbarProps
>(function ToolbarSide(props, ref) {
  let { gutter, offset, ...augmentationToolbarProps } = props
  gutter ??= 0
  offset ??= { right: 0 }
  let [alignToolbar, offsetValue] = Object.entries(offset)[0]
  offsetValue ??= 0

  const { getRectValue } = useObserveSizeContext()
  const alertHeight = getRectValue("alert.height")

  return (
    // NOTE: we need to accept a left/right offset to position the toolbar.
    <Box
      className="ToolbarSide"
      pos="absolute"
      top={0}
      {...{ [alignToolbar]: `${offsetValue}px` }}
      bottom={0}
    >
      <Box zIndex={10001} pos="sticky" top={`${gutter + alertHeight}px`}>
        <AugmentationToolbar ref={ref} {...augmentationToolbarProps} />
      </Box>
    </Box>
  )
})

const ToolbarBottom = React.forwardRef<
  HTMLDivElement,
  AugmentationPositionedToolbarProps
>(function ToolbarBottom(props, ref) {
  let { gutter, ...augmentationToolbarProps } = props
  gutter ??= 0

  return (
    <Box
      className="ToolbarBottom"
      pos="fixed"
      bottom={`${gutter}px`}
      left="50%"
      transform="translateX(-50%)"
      zIndex={10001}
    >
      <AugmentationToolbar
        ref={ref}
        direction="row"
        {...augmentationToolbarProps}
      />
    </Box>
  )
})

const AugmentationPositionedToolbar = React.forwardRef<
  HTMLDivElement,
  AugmentationPositionedToolbarProps
>(function AugmentationPositionedToolbar(props, ref) {
  const { allowOverlay, ...passProps } = props
  const ToolbarComponent = useToolbarPosition(
    {
      bottom: ToolbarBottom,
      side: ToolbarSide
    },
    allowOverlay
  )

  // useBreakpointValue renders the base initially on the first render,
  // no matter what the real breakboint is, so ignore the first output.
  const shouldRender = !!usePrevious(ToolbarComponent)

  return shouldRender ? (
    <ToolbarComponent ref={ref} {...passProps} alwaysVisible />
  ) : null
})

export default AugmentationPositionedToolbar
